import * as React from 'react';

export type FCProps<T> = T extends (arg1: infer U, ...args: unknown[]) => unknown ? U : undefined;

export type NavigationKey = ' ' | 'ArrowDown' | 'ArrowUp' | 'ArrowRight' | 'ArrowLeft' | 'End' | 'Enter' | 'Escape' | 'Home' | 'Tab' | 'PageUp' | 'PageDown';

function isSFC<P>(type: React.ComponentType<P>): type is React.FunctionComponent<P> {
  return !type.prototype.render;
}

export function convertToStatefulComponentClass<P>(componentType: React.ComponentType<P>): React.ComponentClass<P> {
  if (isSFC(componentType)) {
    const statelessComponent: React.FunctionComponent<P> = componentType;
    return class StatefulSFCWrapper extends React.Component<P, {}> {
      public static displayName: string = getComponentName(statelessComponent);
      public render(): React.ReactNode {
        return statelessComponent(this.props);
      }
    };
  } else {
    return componentType;
  }
}

export function mergeEventHandlers<EType extends React.SyntheticEvent>(
  h1: React.EventHandler<EType>,
  h2?: React.EventHandler<EType>
): React.EventHandler<EType> {
  return (h1 && h2 && function (e: EType): void { h1(e); h2(e); })
    || h1 || h2;
}

function isCallbackRef<T>(ref?: React.Ref<T>): ref is ((instance: T | null) => void) {
  return typeof ref === 'function';
}

export const mergeRefs = <T>(...refs: Array<React.Ref<T> | undefined>) => (value: T | null) => {
  refs.forEach(resolvableRef => assignRef(resolvableRef, value));
};

export function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (isCallbackRef(ref)) {
    ref(value);
  } else if (!!ref) {
    (ref as React.MutableRefObject<T>).current = value!;
  }
}

// tslint:disable-next-line:no-any
export function getComponentName(componentType: any): string {
  return componentType.displayName || componentType.name || 'AnonymousComponent';
}

/** Adding this prop tells VizClient's event-utils to completely ignore the element */
export const EventUtilsOptOutAttr: string = 'data-eventutils-optout';
export const EventUtilsOptOut = Object.freeze({
  [EventUtilsOptOutAttr]: true,
});

/** Adding this prop allows us to ignore certain "errors" flagged by the axe accessibility testing framework. TFSID 1193920 */
export const AxeOptOut = Object.freeze({
  'data-axe-ignore': true,
});

/** Marks certain properties of a type as explicitly-disallowed rather than just omitted. */
export type ProhibitPropsFrom<T, K extends keyof T> = {
  [Property in keyof Pick<T, K>]: never
} & Omit<T, K>;
